import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '../config/api';
import { queryKeys } from '../config/queryKeys';
import { summarizeAiMatchResults } from '../utils/aiMatchMessages';
import { isTrackedRunSettled } from '../utils/atsMatchRun';
import { appAlert } from '../utils/appDialog';

const STORAGE_KEY = 'gobunnyy-ats-match-runs';
const AtsMatchRunContext = createContext(null);

function readStoredRuns() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredRuns(runs) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(runs));
  } catch {
    /* ignore quota / private mode */
  }
}

function invalidateMatchViews(queryClient) {
  queryClient.invalidateQueries('all-matches');
  queryClient.invalidateQueries(queryKeys.jobsRoot);
  queryClient.invalidateQueries('jobs');
  queryClient.invalidateQueries('job');
  queryClient.invalidateQueries('job-matches');
  queryClient.invalidateQueries('candidates');
  queryClient.invalidateQueries('candidate');
  queryClient.invalidateQueries(['ai-watch-status']);
  queryClient.invalidateQueries(['ai-watch-log']);
}

function runKey(kind, id) {
  return `${kind}:${id}`;
}

export function AtsMatchRunProvider({ children }) {
  const queryClient = useQueryClient();
  const [runs, setRuns] = useState(readStoredRuns);
  const settledKeys = useRef(new Set());
  const runsRef = useRef(runs);
  const startingKeys = useRef(new Set());

  useEffect(() => {
    runsRef.current = runs;
  }, [runs]);

  useEffect(() => {
    writeStoredRuns(runs);
  }, [runs]);

  const pollKey = runs.map((run) => `${run.key}:${run.runId || 'pending'}`).join('|');

  const { data: pollRows } = useQuery(
    ['ats-match-run-poll', pollKey],
    async () => {
      const rows = await Promise.all(
        runs.map(async (run) => {
          const params = run.kind === 'job' ? { job_id: run.id } : { candidate_id: run.id };
          const { data } = await api.get('/matches/ai-run', { params });
          return { key: run.key, ...data };
        })
      );
      return rows;
    },
    {
      enabled: runs.length > 0,
      refetchInterval: 2500,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      staleTime: 0,
    }
  );

  useEffect(() => {
    if (!pollRows?.length) return;

    const stillRunning = [];
    for (const run of runs) {
      const row = pollRows.find((item) => item.key === run.key);
      const { settled, failed } = isTrackedRunSettled(run, row);

      if (!settled) {
        stillRunning.push(run);
        continue;
      }

      if (settledKeys.current.has(run.key)) continue;
      settledKeys.current.add(run.key);
      invalidateMatchViews(queryClient);
      if (run.notify === false && !failed) continue;
      if (failed) {
        void appAlert(row?.error || 'AI matching failed.');
      } else {
        void appAlert(
          row?.result
            ? summarizeAiMatchResults(row.result)
            : 'AI matching finished in the background.'
        );
      }
    }

    if (stillRunning.length !== runs.length) {
      setRuns(stillRunning);
    }
  }, [pollRows, queryClient, runs]);

  const trackRun = useCallback((kind, id, extra = {}) => {
    const key = runKey(kind, id);
    settledKeys.current.delete(key);
    setRuns((prev) => {
      if (prev.some((run) => run.key === key)) return prev;
      return [
        ...prev,
        {
          key,
          kind,
          id: String(id),
          startedAt: Date.now(),
          ...extra,
        },
      ];
    });
    return key;
  }, []);

  const dropRun = useCallback((key) => {
    setRuns((prev) => prev.filter((run) => run.key !== key));
  }, []);

  const startJobMatch = useCallback(
    async (jobId, { label, notify = true } = {}) => {
      const key = runKey('job', jobId);
      if (runsRef.current.some((run) => run.key === key) || startingKeys.current.has(key)) {
        return { background: true, data: { accepted: true, status: 'running' } };
      }
      startingKeys.current.add(key);
      trackRun('job', jobId, { label, notify });
      try {
        const res = await api.post(`/matches/ai-recompute-job/${jobId}`);
        const data = res.data || {};
        if (data.accepted || res.status === 202) {
          setRuns((prev) =>
            prev.map((run) => (run.key === key ? { ...run, runId: data.run_id } : run))
          );
          return { background: true, data };
        }
        dropRun(key);
        invalidateMatchViews(queryClient);
        if (notify) void appAlert(summarizeAiMatchResults(data));
        return { background: false, data };
      } catch (error) {
        dropRun(key);
        if (notify) void appAlert(error.response?.data?.error || error.message);
        throw error;
      } finally {
        startingKeys.current.delete(key);
      }
    },
    [dropRun, queryClient, trackRun]
  );

  const startCandidateMatch = useCallback(
    async (candidateId, { label, notify = true } = {}) => {
      const key = runKey('candidate', candidateId);
      if (runsRef.current.some((run) => run.key === key) || startingKeys.current.has(key)) {
        return { background: true, data: { accepted: true, status: 'running' } };
      }
      startingKeys.current.add(key);
      trackRun('candidate', candidateId, { label, notify });
      try {
        const res = await api.post(`/matches/ai-recompute-candidate/${candidateId}`);
        const data = res.data || {};
        if (data.accepted || res.status === 202) {
          setRuns((prev) =>
            prev.map((run) => (run.key === key ? { ...run, runId: data.run_id } : run))
          );
          return { background: true, data };
        }
        dropRun(key);
        invalidateMatchViews(queryClient);
        if (notify) void appAlert(summarizeAiMatchResults(data));
        return { background: false, data };
      } catch (error) {
        dropRun(key);
        if (notify) void appAlert(error.response?.data?.error || error.message);
        throw error;
      } finally {
        startingKeys.current.delete(key);
      }
    },
    [dropRun, queryClient, trackRun]
  );

  const value = useMemo(() => {
    const matchingJobId = runs.find((run) => run.kind === 'job')?.id || null;
    const matchingCandidateId = runs.find((run) => run.kind === 'candidate')?.id || null;
    return {
      runs,
      anyMatching: runs.length > 0,
      matchingJobId,
      matchingCandidateId,
      isJobMatching: (jobId) => runs.some((run) => run.kind === 'job' && String(run.id) === String(jobId)),
      isCandidateMatching: (candidateId) =>
        runs.some((run) => run.kind === 'candidate' && String(run.id) === String(candidateId)),
      startJobMatch,
      startCandidateMatch,
    };
  }, [runs, startCandidateMatch, startJobMatch]);

  return <AtsMatchRunContext.Provider value={value}>{children}</AtsMatchRunContext.Provider>;
}

export function useAtsMatchRun() {
  const value = useContext(AtsMatchRunContext);
  if (!value) {
    return {
      runs: [],
      anyMatching: false,
      matchingJobId: null,
      matchingCandidateId: null,
      isJobMatching: () => false,
      isCandidateMatching: () => false,
      startJobMatch: async () => ({ background: false, data: {} }),
      startCandidateMatch: async () => ({ background: false, data: {} }),
    };
  }
  return value;
}
